import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { api, ApiError } from './api';

let isConfigured = false;

export async function initGoogleMaps(): Promise<{
  Map: typeof google.maps.Map;
  geometryLib: google.maps.GeometryLibrary;
  markerLib: google.maps.MarkerLibrary;
  mapId: string;
}> {
  const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
  if (!key) {
    throw new Error('Google Maps API key is not configured in VITE_GOOGLE_MAPS_API_KEY.');
  }

  if (!isConfigured) {
    setOptions({
      key,
      v: 'weekly',
    });
    isConfigured = true;
  }

  const [mapsLib, geometryLib, markerLib] = await Promise.all([
    importLibrary('maps') as Promise<google.maps.MapsLibrary>,
    importLibrary('geometry') as Promise<google.maps.GeometryLibrary>,
    importLibrary('marker') as Promise<google.maps.MarkerLibrary>,
  ]);

  return {
    Map: mapsLib.Map,
    geometryLib,
    markerLib,
    // Advanced markers need a Map ID. DEMO_MAP_ID is Google's test-only ID; set a real one for production.
    mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID',
  };
}

export interface RouteAlternative {
  index: number;
  summary: string;
  distanceKm: number;
  durationText: string;
  encodedPolyline?: string;
  pathPoints?: Array<{ lat: number; lng: number }>;
}

// In-memory cache to prevent redundant API calls
const routesCache = new Map<string, RouteAlternative[]>();

function getCacheKey(origin: string, destination: string, vehicleType: string): string {
  return `${origin.trim().toLowerCase()}|${destination.trim().toLowerCase()}|${vehicleType.trim().toLowerCase()}`;
}

/**
 * Computes routes and alternative paths via our backend proxy endpoint (/api/v1/mileage/routes).
 * The backend proxies the request to Google Routes API v2 securely, routing for the given vehicle.
 */
export async function computeRoutes(
  origin: string,
  destination: string,
  vehicleType: string,
): Promise<RouteAlternative[]> {
  const cacheKey = getCacheKey(origin, destination, vehicleType);
  const cached = routesCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const res = await api.post<{ routes: RouteAlternative[] }>('/mileage/routes', {
      origin: origin.trim(),
      destination: destination.trim(),
      vehicleType,
    });

    const routes = res.routes || [];
    routesCache.set(cacheKey, routes);
    return routes;
  } catch (err: unknown) {
    if (err instanceof ApiError) {
      if (
        err.code === 'QUOTA_EXCEEDED' ||
        err.status === 429 ||
        err.message.toLowerCase().includes('quota')
      ) {
        throw new Error('Google Maps quota reached. Try again later.', { cause: err });
      }
      throw new Error(err.message, { cause: err });
    }
    throw err;
  }
}


